<template>
  <div class="map-container">
    <div :id="mapId" class="map-content">
      <!-- 底图切换控件 -->
      <div class="basemap-control">
        <button v-for="type in basemapTypes" :key="type.value" @click="switchBaseMap(type.value)"
          :class="{ active: currentBaseMap === type.value }" style="width: 105px;">
          {{ type.label }}
        </button>
      </div>
      <div class="edit-control">
        <a-button :type="isEditing ? 'danger' : 'primary'" @click="handleEditZone" v-if="currentId">
          {{ isEditing ? '取消编辑' : '编辑片区' }}
        </a-button>
        <a-button :type="isEditingAdd ? 'danger' : 'primary'" @click="handleEditADDZone" v-else>
          {{ isEditingAdd ? '取消新增' : '新增片区' }}
        </a-button>
        <a-button type="primary" @click="saveZone" v-if="(isEditing || isEditingAdd) && editPoints.length >= 3">
          保存片区
        </a-button>
        <a-button type="danger" @click="quitRedact">退出编辑</a-button>
      </div>
      <!-- 片区级别切换控件 -->
      <div class="level-control">
        <a-select v-model="currentLevel" style="width: 105px" @change="handleLevelChange" placeholder="选择级别"
          size="small">
          <a-select-option v-for="level in layerLevelList" :key="level" :value="level">
            {{ level }} 级片区
          </a-select-option>
        </a-select>
      </div>

      <!-- 图层显示/隐藏控件 -->
      <div class="layer-control">
        <div class="control-title">图层控制</div>
        <div class="layer-toggle" v-for="(config, layerId) in layerConfigs" :key="layerId">
          <a-checkbox v-model="visibleLayers[layerId]" @change="toggleLayerVisibility(layerId)">
            {{ getLayerLabel(layerId) }}
          </a-checkbox>
        </div>
      </div>
    </div>
    <div class="global-loading" v-if="saving">
      <a-spin size="large" tip="正在处理，请稍候..." />
    </div>
  </div>

</template>

<script>
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import ImageWMS from 'ol/source/ImageWMS';
import VectorSource from 'ol/source/Vector';
import { Point, LineString, Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { Translate } from 'ol/interaction';
import * as olProj from 'ol/proj';
import { getUUID } from '@/utils/util';
import storage from 'store';
import { LonLat } from '@/store/mutation-types';
import { Select, Checkbox, Button, message } from 'ant-design-vue';
import { unByKey } from 'ol/Observable';

export default {
  name: 'zoneMap',
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-checkbox': Checkbox,
    'a-button': Button
  },
  props: {
    layerLevelList: {
      type: Array,
      required: true,
      default: () => [1, 2, 3]
    },
    currentId: {
      type: String,
    },
    geoJson: {
      type: String,
    },
    level: {
      type: String,
      default: '1'
    },
    parentZoneId: {
      type: String,
      default: ''
    }
  },
  watch: {
    isEditing (newValue, oldValue) {
      if (!newValue) {
        this.zoneId = ''
      }
    }
  },
  data () {
    return {
      mapId: getUUID(),
      map: null,
      gcjMercator: null,
      currentBaseMap: 'gaode',
      currentLevel: 1,
      visibleLayers: {},
      isEditing: false,
      vectorSource: null,
      vectorLayer: null,
      translateInteraction: null,
      editFeatures: [],
      editPoints: [],
      isInEditMode: false,
      isEditingAdd: false,
      clickTypeList: [],
      wmsLayers: {},
      baseLayer: null,
      midPointFeatures: [],
      zoneId: '',
      saving: false,
      basemapTypes: [
        { value: 'gaode', label: '高德地图' },
        { value: 'tianditu_img', label: '天地影像' },
        // { value: 'arcgis', label: 'ArcGIS影像' },
      ],
      clickHandler: null,
      isCreatingPolygon: false,
      isPolygonComplete: false,
      updateGeomUrl: 'zone-metering/zone-metering/updateGeom',

      baseMapUrls: {
        gaode: 'http://wprd0{1-4}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&style=7&x={x}&y={y}&z={z}',
        tianditu_img: () => localStorage.getItem('env') === '生产'
          ? 'https://t7.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=bbb8e87c166f332a270fa223ba022abb'
          : 'http://t7.tianditu.com/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=bbb8e87c166f332a270fa223ba022abb',
        arcgis: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      },

      layerConfigs: {
        gis_pipe_line: {
          workspace: 'smart_water',
          dataSource: 'smart_water:gis_pipe_line',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}'`,
          label: '管线图层'
        },
        zone_associated_point_valve: {
          workspace: 'smart_water',
          dataSource: 'smart_water:zone_associated_point',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}' and appendage = '分界阀门'`,
          label: '分界阀门'
        },
        zone_associated_point_meter: {
          workspace: 'smart_water',
          dataSource: 'smart_water:remote_water_meter',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}'`,
          label: '考核流量计'
        },
        zone_metering: {
          workspace: 'smart_water',
          dataSource: 'smart_water:zone_metering',
          showFilterCondition: `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}' and level = 1`,
          label: '分区全览'
        }
      }
    };
  },
  mounted () {

    this.initVisibleLayers();
    this.initMap();

    this.handleLevelChange(Number(this.level))
    if (!this.currentId) {
      this.getGeoJson();
    }
  },

  beforeDestroy () {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = null;
    }
    this.removeClickHandler();
  },
  methods: {
    getGeoJson () {
      if (this.geoJson) {
        try {
          const geoData = JSON.parse(this.geoJson);
          if (geoData.type === 'Polygon' && geoData.coordinates && geoData.coordinates.length > 0) {
            // 1. 进入新增模式（和点击"新增片区"按钮完全一致）
            this.isEditingAdd = true;
            this.translateInteraction.setActive(true);
            this.removeClickHandler(); // 移除旧的事件（如果有）
            this.clickHandler = this.map.on('click', this.handleMapClickForAdd.bind(this)); // 绑定点击事件

            // 2. 清空现有编辑点
            this.clearAllEditFeatures();

            // 3. 添加所有点（模拟手动打点）
            const coords = geoData.coordinates[0]; // 去掉闭合点
            coords.forEach(coord => {
              const newEditPoint = this.createEditPointFeature(coord);
              this.editPoints.push(newEditPoint);
              this.editFeatures.push(newEditPoint);
              this.vectorSource.addFeature(newEditPoint);
            });

            // 4. 更新线和面（和手动打点逻辑一致）
            this.updateLineFeature();
            this.updatePolygonFeature(false); // 不自动闭合
            this.isCreatingPolygon = true;
            this.isInEditMode = true;

            // 5. 确保可以拖动（新增模式默认允许拖动点）
            this.translateInteraction.setActive(true);
          }
        } catch (e) {
          console.error('解析geoJson失败:', e);
        }
      }
    },
    initVisibleLayers () {
      Object.keys(this.layerConfigs).forEach(layerId => {
        this.$set(this.visibleLayers, layerId, true);
      });
    },

    getLayerLabel (layerId) {
      return this.layerConfigs[layerId]?.label || layerId;
    },

    toggleLayerVisibility (layerId) {
      const layer = this.wmsLayers[layerId];
      if (layer) {
        layer.setVisible(this.visibleLayers[layerId]);
      } else if (this.visibleLayers[layerId]) {
        if (layerId === 'zone_metering') {
          this.updateZoneMeteringLayer();
        } else {
          this.loadGeoserverLayer(layerId);
        }
      }
    },

    initMap () {
      this.setupGcjProjection();
      const center = this.getInitialCenter();

      this.vectorSource = new VectorSource();
      this.vectorLayer = new VectorLayer({
        source: this.vectorSource,
        style: this.getVectorStyle()
      });
      this.map = new Map({
        view: new View({
          center: center,
          projection: 'EPSG:4326',
          zoom: 12,
          maxZoom: 28,
          minZoom: 1
        }),
        target: this.mapId,
        layers: []
      });

      this.switchBaseMap(this.currentBaseMap);
      this.map.addLayer(this.vectorLayer);

      this.translateInteraction = new Translate({
        features: this.vectorSource.getFeaturesCollection(),
        filter: (feature) => {
          return feature.get('type') === 'edit-point' && feature.get('isEditFeature');
        }
      });
      this.map.addInteraction(this.translateInteraction);
      this.translateInteraction.setActive(false);

      this.vectorSource.on('changefeature', this.updateConnectedGeometries);

      this.map.on('click', (evt) => {
        if (this.isEditing) {
          const feature = this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
            return feature.get('type') === 'mid-point' ? feature : null;
          });

          if (feature) {
            this.handleMidPointClick(feature);
          }
        }
      });

      this.map.on('contextmenu', (evt) => {
        if (!this.isEditing && !this.isEditingAdd) return;
        evt.preventDefault();

        const feature = this.map.forEachFeatureAtPixel(evt.pixel, (feature) => {
          return feature.get('type') === 'edit-point' && feature.get('isEditFeature') ? feature : null;
        });

        if (feature) {
          this.handleDeletePoint(feature);
        }
      });
      this.loadAllGeoserverLayers();
      requestAnimationFrame(() => {
        this.map.renderSync();
        this.map.updateSize();
      });
    },

    removeClickHandler () {
      if (this.clickHandler) {
        unByKey(this.clickHandler);
        this.clickHandler = null;
      }
    },

    handleMapClickForAdd (evt) {
      if (!this.isEditingAdd) return;

      const coordinate = evt.coordinate;

      const newEditPoint = this.createEditPointFeature(coordinate);
      this.editPoints.push(newEditPoint);
      this.editFeatures.push(newEditPoint);
      this.vectorSource.addFeature(newEditPoint);

      this.updateLineFeature();

      if (this.editPoints.length >= 3) {
        this.updatePolygonFeature(false);
        this.isCreatingPolygon = true;
      }

      this.isInEditMode = true;
    },

    updateLineFeature () {
      const oldLine = this.editFeatures.find(f => f.get('type') === 'edit-line');
      if (oldLine) {
        this.vectorSource.removeFeature(oldLine);
        this.editFeatures = this.editFeatures.filter(f => f !== oldLine);
      }

      if (this.editPoints.length >= 2) {
        const lineCoords = this.editPoints.map(p => p.getGeometry().getCoordinates());
        const lineFeature = this.createEditLineStringFeature(lineCoords);
        this.editFeatures.push(lineFeature);
        this.vectorSource.addFeature(lineFeature);
      }
    },

    updatePolygonFeature (autoClose = false) {
      const oldPolygon = this.editFeatures.find(f => f.get('type') === 'edit-polygon');
      if (oldPolygon) {
        this.vectorSource.removeFeature(oldPolygon);
        this.editFeatures = this.editFeatures.filter(f => f !== oldPolygon);
      }

      if (this.editPoints.length >= 3) {
        const polygonCoords = [this.editPoints.map(p => p.getGeometry().getCoordinates())];

        if (autoClose || this.isEditing) {
          polygonCoords[0].push(polygonCoords[0][0]);
        }

        const polygonFeature = this.createEditPolygonFeature(polygonCoords);
        this.editFeatures.push(polygonFeature);
        this.vectorSource.addFeature(polygonFeature);
      }
    },

    getVectorStyle () {
      return (feature) => {
        const type = feature.get('type');
        const isEdit = feature.get('isEditFeature');
        const isMidPoint = feature.get('type') === 'mid-point';
        const isAddMode = this.isEditingAdd;

        if (isMidPoint) {
          return new Style({
            image: new CircleStyle({
              radius: 6,
              fill: new Fill({ color: 'rgba(0, 0, 255, 0.5)' }),
              stroke: new Stroke({ color: 'rgba(0, 0, 255, 0.8)', width: 1 })
            })
          });
        }

        if (isEdit) {
          // 新增模式下，只显示点和面，不显示线
          return new Style({
            image: type === 'edit-point' ? new CircleStyle({
              radius: 8,
              fill: new Fill({ color: 'rgba(255, 0, 0, 0.8)' }),
              stroke: new Stroke({ color: 'black', width: 2 })
            }) : undefined,
            stroke: type === 'edit-polygon' ? new Stroke({
              color: 'rgba(0, 255, 0, 0.5)',
              width: 3,
              lineDash: [5, 5]
            }) : undefined,
            fill: type === 'edit-polygon' ? new Fill({
              color: 'rgba(0, 255, 0, 0.1)'
            }) : undefined
          });
        }

        const styles = {
          point: new Style({
            image: new CircleStyle({
              radius: 7,
              fill: new Fill({ color: 'red' }),
              stroke: new Stroke({ color: 'black', width: 2 })
            })
          }),
          line: new Style({
            stroke: new Stroke({
              color: 'blue',
              width: 2
            })
          }),
          polygon: new Style({
            fill: new Fill({
              color: 'rgba(0, 255, 0, 0.2)'
            }),
            stroke: new Stroke({
              color: 'green',
              width: 2
            })
          })
        };
        return styles[type];
      };
    },

    setupGcjProjection () {
      const gcj02Extent = [-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
      this.gcjMercator = new olProj.Projection({
        code: 'GCJ-02',
        extent: gcj02Extent,
        units: 'm'
      });
      olProj.addProjection(this.gcjMercator);

      if (window.projzh) {
        olProj.addCoordinateTransforms('EPSG:4326', this.gcjMercator, window.projzh.ll2gmerc, window.projzh.gmerc2ll);
        olProj.addCoordinateTransforms('EPSG:3857', this.gcjMercator, window.projzh.smerc2gmerc, window.projzh.gmerc2smerc);
      }
    },

    getInitialCenter () {
      if (this.geo) {
        try {
          return JSON.parse(this.geo).coordinates;
        } catch (e) {
          console.error('解析geo属性失败:', e);
        }
      }
      return storage.get(LonLat) || [106.456878, 29.541145];
    },

    switchBaseMap (type) {
      if (!this.basemapTypes.some(t => t.value === type)) {
        console.warn(`无效的底图类型: ${type}`);
        return;
      }

      if (this.baseLayer) {
        this.map.removeLayer(this.baseLayer);
        this.baseLayer = null;
      }

      this.currentBaseMap = type;
      const layer = this.createBaseLayer(type);

      if (layer) {
        this.baseLayer = layer;
        this.map.getLayers().insertAt(0, layer);
      }
    },

    createBaseLayer (type) {
      const url = typeof this.baseMapUrls[type] === 'function'
        ? this.baseMapUrls[type]()
        : this.baseMapUrls[type];

      const isGcj = type === 'gaode';

      return new TileLayer({
        properties: {
          name: '底图',
          title: this.basemapTypes.find(t => t.value === type).label,
          isBaseLayer: true
        },
        source: new XYZ({
          projection: isGcj ? this.gcjMercator : 'EPSG:3857',
          crossOrigin: 'anonymous',
          url: url,
          maxZoom: 18,
          tileSize: 256
        })
      });
    },

    handleLevelChange (level) {
      console.log(level);

      if (this.isEditing) {
        this.isEditing = false;
        this.translateInteraction.setActive(false);
        this.clearAllEditFeatures();
      }
      this.currentLevel = level;
      this.updateZoneMeteringLayer();
    },

    updateZoneMeteringLayer () {
      const config = this.layerConfigs.zone_metering;
      if (!config) return;
      config.showFilterCondition =
        `is_deleted = false and tenant_id = '${localStorage.getItem('tenant-user').split('_')[0]}' and level = ${this.currentLevel}`;

      this.loadGeoserverLayer('zone_metering');
    },

    loadAllGeoserverLayers () {
      this.updateZoneMeteringLayer();
      this.loadGeoserverLayer('gis_pipe_line');
      this.loadGeoserverLayer('zone_associated_point_valve');
      this.loadGeoserverLayer('zone_associated_point_meter');
    },

    loadGeoserverLayer (layerId) {


      const config = this.layerConfigs[layerId];
      if (!config) {
        console.warn(`未找到图层配置: ${layerId}`);
        return;
      }

      if (this.wmsLayers[layerId]) {
        this.map.removeLayer(this.wmsLayers[layerId]);
      }

      const wmsSource = new ImageWMS({
        url: `/api/geoserver/${config.workspace}/wms?_t=${Date.now()}`,
        params: {
          'LAYERS': config.dataSource,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.0',
          'CQL_FILTER': config.showFilterCondition
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous'
      });
      const wmsLayer = new ImageLayer({
        source: wmsSource,
        properties: {
          id: layerId,
          name: config.label || layerId
        },
        visible: this.visibleLayers[layerId] !== false,
        zIndex: 1
      });
      if (layerId == 'zone_metering') {
        this.clickWMS(wmsSource, layerId, wmsLayer);
      }
      this.wmsLayers[layerId] = wmsLayer;
      this.map.addLayer(wmsLayer);
    },

    clickWMS (wmsSource, layerId, wmsLayer) {
      let that = this;
      unByKey(this.clickTypeList[layerId]);
      this.clickTypeList[layerId] = this.map.on('singleclick', function (e) {
        const url = wmsSource.getFeatureInfoUrl(
          e.coordinate,
          that.map.getView().getResolution(),
          'EPSG:4326',
          { INFO_FORMAT: 'application/json' }
        );
        if (url) {
          fetch(url)
            .then((response) => response.json())
            .then((data) => that.getZoneInfo(data.features[0]));
        }
      });
    },

    getZoneInfo (data) {
      if (this.isEditing) {
        if (!data) { return; }
        if (this.zoneId == data.id) {
          return;
        }
        this.zoneId = data.id;
        this.clearAllEditFeatures();
        const coordinates = data.geometry.coordinates;
        if (data.geometry.type === 'Polygon') {
          this.createEditPolygonFeatures(coordinates[0]);
        } else if (data.geometry.type === 'LineString') {
          this.createEditLineStringFeatures(coordinates);
        } else if (data.geometry.type === 'Point') {
          this.createEditPointFeature(coordinates);
        }
        this.isInEditMode = true;
      }
    },

    createEditPolygonFeatures (coords) {
      this.clearMidPoints();

      this.editPoints = coords.slice(0, -1).map(coord => this.createEditPointFeature(coord));
      const lineFeature = this.createEditLineStringFeature(coords);
      const polygonFeature = this.createEditPolygonFeature([coords]);

      this.editFeatures = [...this.editPoints, lineFeature, polygonFeature];
      this.vectorSource.addFeatures(this.editFeatures);

      this.addMidPointsForPolygon(coords);
    },

    createEditLineStringFeatures (coords) {
      this.clearMidPoints();

      this.editPoints = coords.map(coord => this.createEditPointFeature(coord));
      const lineFeature = this.createEditLineStringFeature(coords);

      this.editFeatures = [...this.editPoints, lineFeature];
      this.vectorSource.addFeatures(this.editFeatures);

      this.addMidPointsForLineString(coords);
    },

    createEditPointFeature (coords) {
      const point = new Point(coords);
      const feature = new Feature({
        geometry: point,
        type: 'edit-point',
        isEditFeature: true
      });
      return feature;
    },

    createEditLineStringFeature (coords) {
      const line = new LineString(coords);
      const feature = new Feature({
        geometry: line,
        type: 'edit-line',
        isEditFeature: true
      });
      return feature;
    },

    createEditPolygonFeature (coords) {
      const polygon = new Polygon(coords);
      const feature = new Feature({
        geometry: polygon,
        type: 'edit-polygon',
        isEditFeature: true
      });
      return feature;
    },

    addMidPointsForPolygon (coords) {
      for (let i = 0; i < coords.length - 1; i++) {
        const start = coords[i];
        const end = coords[(i + 1) % (coords.length - 1)];
        const midPoint = this.calculateMidPoint(start, end);

        const pointFeature = new Feature({
          geometry: new Point(midPoint),
          type: 'mid-point',
          edgeIndex: i,
          isMidPoint: true
        });

        this.midPointFeatures.push(pointFeature);
        this.vectorSource.addFeature(pointFeature);
      }
    },

    addMidPointsForLineString (coords) {
      for (let i = 0; i < coords.length - 1; i++) {
        const start = coords[i];
        const end = coords[i + 1];
        const midPoint = this.calculateMidPoint(start, end);

        const pointFeature = new Feature({
          geometry: new Point(midPoint),
          type: 'mid-point',
          edgeIndex: i,
          isMidPoint: true
        });

        this.midPointFeatures.push(pointFeature);
        this.vectorSource.addFeature(pointFeature);
      }
    },

    calculateMidPoint (start, end) {
      return [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2
      ];
    },

    clearMidPoints () {
      this.midPointFeatures.forEach(feature => {
        this.vectorSource.removeFeature(feature);
      });
      this.midPointFeatures = [];
    },

    updateConnectedGeometries (event) {
      if (!this.isInEditMode) return;

      const movedFeature = event.feature;
      if (movedFeature.get('type') !== 'edit-point') return;

      const pointCoords = this.editPoints.map(point => point.getGeometry().getCoordinates());

      const editLine = this.editFeatures.find(f => f.get('type') === 'edit-line');
      if (editLine) {
        const shouldClose = this.isEditing && !this.isEditingAdd;
        editLine.getGeometry().setCoordinates(
          shouldClose ? [...pointCoords, pointCoords[0]] : pointCoords
        );
      }

      const editPolygon = this.editFeatures.find(f => f.get('type') === 'edit-polygon');
      if (editPolygon) {
        const shouldClose = this.isEditing && !this.isEditingAdd;
        editPolygon.getGeometry().setCoordinates(
          shouldClose ? [[...pointCoords, pointCoords[0]]] : [pointCoords]
        );
      }

      if (this.isEditing) {
        this.updateMidPoints();
      }
    },

    updateMidPoints () {
      this.clearMidPoints();

      const pointCoords = this.editPoints.map(point => point.getGeometry().getCoordinates());

      if (this.editFeatures.some(f => f.get('type') === 'edit-polygon')) {
        this.addMidPointsForPolygon([...pointCoords, pointCoords[0]]);
      } else {
        this.addMidPointsForLineString(pointCoords);
      }
    },

    handleMidPointClick (feature) {
      if (!this.isEditing) return;

      const edgeIndex = feature.get('edgeIndex');
      const midPointCoords = feature.getGeometry().getCoordinates();

      const newEditPoint = this.createEditPointFeature(midPointCoords);

      const isPolygon = this.editFeatures.some(f => f.get('type') === 'edit-polygon');
      const insertIndex = isPolygon ?
        (edgeIndex + 1) % this.editPoints.length :
        edgeIndex + 1;

      this.editPoints.splice(insertIndex, 0, newEditPoint);
      this.editFeatures.push(newEditPoint);
      this.vectorSource.addFeature(newEditPoint);

      const editLine = this.editFeatures.find(f => f.get('type') === 'edit-line');
      if (editLine) {
        const lineCoords = isPolygon ?
          [...this.editPoints.map(p => p.getGeometry().getCoordinates()), this.editPoints[0].getGeometry().getCoordinates()] :
          this.editPoints.map(p => p.getGeometry().getCoordinates());
        editLine.getGeometry().setCoordinates(lineCoords);
      }

      const editPolygon = this.editFeatures.find(f => f.get('type') === 'edit-polygon');
      if (editPolygon) {
        editPolygon.getGeometry().setCoordinates([
          [...this.editPoints.map(p => p.getGeometry().getCoordinates()), this.editPoints[0].getGeometry().getCoordinates()]
        ]);
      }

      this.updateMidPoints();
    },

    handleDeletePoint (feature) {
      if (!this.isEditing && !this.isEditingAdd) return;

      const isPolygon = this.isCreatingPolygon;
      const minPoints = isPolygon ? 3 : 2;

      if (this.editPoints.length <= minPoints) {
        message.warning(isPolygon ? '多边形至少需要3个顶点' : '线段至少需要2个顶点');
        return;
      }

      const index = this.editPoints.indexOf(feature);
      if (index === -1) return;

      // 删除点
      this.editPoints.splice(index, 1);

      // 从编辑特征中移除
      const featureIndex = this.editFeatures.indexOf(feature);
      if (featureIndex !== -1) {
        this.editFeatures.splice(featureIndex, 1);
      }

      // 从源中移除特征
      this.vectorSource.removeFeature(feature);

      // 统一调用更新方法，而不是直接操作几何图形
      this.updateLineFeature();

      // 如果是多边形模式（新增或编辑），更新面
      if (this.isCreatingPolygon || this.isEditing) {
        this.updatePolygonFeature(this.isEditing); // 编辑模式下自动闭合
      }

      // 如果是编辑模式，还需要更新中点
      if (this.isEditing) {
        this.updateMidPoints();
      }
    },

    clearAllEditFeatures () {
      if (!this.vectorSource) return;
      this.editFeatures.forEach(feature => {
        this.vectorSource.removeFeature(feature);
      });

      this.clearMidPoints();

      this.editFeatures = [];
      this.editPoints = [];
      this.isInEditMode = false;
      this.isCreatingPolygon = false;
      this.isPolygonComplete = false;
    },

    handleEditZone () {
      this.isEditing = !this.isEditing;
      this.translateInteraction.setActive(this.isEditing);

      if (!this.isEditing) {
        this.clearAllEditFeatures();
      }
    },

    handleEditADDZone () {
      this.isEditingAdd = !this.isEditingAdd;

      if (this.isEditingAdd) {
        this.isEditing = false;
        this.translateInteraction.setActive(true);
        this.clearAllEditFeatures();

        this.removeClickHandler();
        this.clickHandler = this.map.on('click', this.handleMapClickForAdd.bind(this));
      } else {
        this.removeClickHandler();
        this.clearAllEditFeatures();
        this.translateInteraction.setActive(false);
      }
    },

    saveNewZone () {
      if (this.editPoints.length < 3) {
        message.warning('至少需要3个点才能创建片区');
        return;
      }

      this.updatePolygonFeature(true);

      const coordinates = this.editPoints.map(point => point.getGeometry().getCoordinates());
      coordinates.push(coordinates[0]);

      const geometry = {
        type: 'Polygon',
        coordinates: [coordinates]
      };
      this.isEditingAdd = false;
      this.removeClickHandler();
      this.clearAllEditFeatures();

      this.updateZoneMeteringLayer();
    },
    saveZone () {
      if (this.editPoints.length < 3) {
        message.warning('至少需要3个点才能创建片区');
        return;
      }

      // 获取所有点位的坐标数组
      const coordinates = this.editPoints.map(point => point.getGeometry().getCoordinates());

      // 如果是多边形，需要闭合（添加第一个点作为最后一个点）
      if (this.isCreatingPolygon || this.isEditing) {
        coordinates.push(coordinates[0]);
      }
      coordinates.pop();
      // 构建几何对象
      const geometry = {
        type: 'Polygon',
        coordinates: [coordinates]
      };

      console.log('点位数组:', coordinates);
      console.log('几何对象:', geometry);
      if (!this.currentId) {
        message.success('片区保存成功');
        this.$emit('insert-form-geoJson', JSON.stringify(geometry));
      } else {
        // 判断当前编辑的片区ID与父组件的zoneId是否一样     

        const currentZoneId = this.zoneId.replace('zone_metering.', '');
        console.log(currentZoneId, this.parentZoneId);
        if (currentZoneId === this.parentZoneId) {
          // 如果是同一个片区，传递geoJson给父组件进行同步修改
          message.success('片区保存成功');
          this.$emit('insert-form-geoJson', JSON.stringify(geometry));
        } else {
          // 如果不是同一个片区，调用接口保存
          const params = { id: currentZoneId, geoJson: JSON.stringify(geometry) }
          this.saving = true;
          this.$http.put(this.updateGeomUrl, params).then((res) => {
            if (res.code == 0) {
              message.success('片区保存成功');
              // 1. 刷新片区图层
              this.updateZoneMeteringLayer();

              // 2. 退出编辑模式
              this.isEditing = false;
              this.translateInteraction.setActive(false);
              this.clearAllEditFeatures();

            }
          }).finally(() => {
            this.saving = false;
          })
        }
      }

      // 不重置编辑状态，保持当前编辑状态
      // 仅刷新图层（如果需要）
      // this.updateZoneMeteringLayer();
    },
    quitRedact () {
      this.$emit('quit-redact', '');
    },

  }
};
</script>

<style lang="less" scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;

  .map-content {
    width: 100%;
    height: 100%;
  }
}

.basemap-control {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);

  button {
    display: block;
    width: 100%;
    margin: 2px 0;
    padding: 5px 10px;
    border: none;
    background: #f8f8f8;
    cursor: pointer;
    border-radius: 3px;
    font-size: 12px;

    &:hover {
      background: #e0e0e0;
    }

    &.active {
      background: #3388ff;
      color: white;
    }
  }
}

.level-control {
  position: absolute;
  top: 130px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);

  /deep/ .ant-select {
    width: 120px;

    .ant-select-selection {
      border: 1px solid #d9d9d9;
      border-radius: 3px;
      height: 28px;

      &:hover {
        border-color: #40a9ff;
      }
    }

    .ant-select-selection__rendered {
      line-height: 26px;
    }

    .ant-select-selection-selected-value {
      padding-right: 16px;
    }
  }
}

.layer-control {
  position: absolute;
  top: 190px;
  left: 10px;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  max-height: 300px;
  overflow-y: auto;

  .control-title {
    font-weight: bold;
    margin-bottom: 8px;
    font-size: 13px;
    color: #333;
    text-align: center;
  }

  .layer-toggle {
    margin: 5px 0;
    white-space: nowrap;

    /deep/ .ant-checkbox-wrapper {
      font-size: 12px;
    }
  }
}

.edit-control {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: white;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 300px;

  /deep/ .ant-btn {
    padding: 0 15px;
    height: 32px;
    font-size: 14px;
    transition: all 0.3s;
    flex-shrink: 0;
  }
}

.global-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .ant-spin-text {
    margin-top: 10px;
    font-size: 16px;
  }
}
</style>